import React, { useMemo, useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, BookOpenIcon, ClockIcon, SwordIcon } from 'lucide-react';
import ArticleCard from '../../components/Articles/ArticleCard';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import { useDarkMode } from '../../context/DarkModeContext';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface Article {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image?: string;
  featured?: boolean;
}
const mysteryArticles: Article[] = [{
  id: 1,
  title: 'Crafting the Perfect Plot Twist',
  excerpt: 'Expert tips on writing unexpected reveals that keep readers guessing without feeling cheated. Deconstructing famous twists.',
  author: 'James Patterson',
  date: 'October 8, 2023',
  category: 'Craft',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1509347528160-9a9e33742cdb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  featured: true
}, {
  id: 2,
  title: 'Psychology of Suspense',
  excerpt: 'Understanding the psychological elements that make thrillers effective — fear, anticipation, and the art of withholding information.',
  author: 'Lisa Gardner',
  date: 'October 5, 2023',
  category: 'Analysis',
  readTime: '10 min read',
  image: 'https://images.unsplash.com/photo-1590856029826-c7a73142bbf1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 3,
  title: 'Cozy Mystery: The Genre That Keeps Growing',
  excerpt: "From small-town sleuths to culinary detectives, cozy mystery continues to outperform expectations. An inside look at the genre's loyal readership.",
  author: 'Claire MacAllister',
  date: 'September 25, 2023',
  category: 'Trends',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1551269901-5c5e14c25df7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 4,
  title: 'Writing the Unreliable Narrator',
  excerpt: 'How to effectively use unreliable narration to increase tension and doubt in your psychological thriller.',
  author: 'Thriller Guild',
  date: 'September 20, 2023',
  category: 'Craft',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 5,
  title: 'Police Procedural vs. Amateur Sleuth',
  excerpt: 'The key differences in structure, tone, and reader expectations between these two pillars of crime fiction.',
  author: 'Detective Writers',
  date: 'September 15, 2023',
  category: 'Analysis',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1587876931567-564ce588bfbd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 6,
  title: 'Domestic Thrillers: Why We Love Them',
  excerpt: 'Exploring the enduring popularity of thrillers set in the home, and what they say about our modern anxieties.',
  author: 'Book Review Weekly',
  date: 'September 10, 2023',
  category: 'Trends',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1516575334481-f85287c2c82d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
const CATEGORIES = ['All', 'Craft', 'Trends', 'Analysis', 'Marketing', 'Interviews'];
const READ_TIMES = ['All', 'Under 8 min', '8–12 min', '12+ min'];
const parseMinutes = (readTime: string): number => {
  const match = readTime.match(/(\d+)/);
  return match ? parseInt(match[1]) : 0;
};
const MysteryThrillerArticles = () => {
  const {
    darkMode
  } = useDarkMode();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeReadTime, setActiveReadTime] = useState('All');
  const featured = mysteryArticles.find(a => a.featured)!;
  const rest = mysteryArticles.filter(a => !a.featured);
  const filtered = useMemo(() => {
    return rest.filter(article => {
      const matchesSearch = !searchQuery || article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) || article.author.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
      const mins = parseMinutes(article.readTime);
      const matchesReadTime = activeReadTime === 'All' || activeReadTime === 'Under 8 min' && mins < 8 || activeReadTime === '8–12 min' && mins >= 8 && mins <= 12 || activeReadTime === '12+ min' && mins > 12;
      return matchesSearch && matchesCategory && matchesReadTime;
    });
  }, [searchQuery, activeCategory, activeReadTime, rest]);
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    value: activeCategory,
    onChange: val => setActiveCategory(val),
    options: CATEGORIES.map(c => ({
      value: c,
      label: c
    }))
  }, {
    id: 'readtime',
    title: 'Read Time',
    type: 'radio',
    value: activeReadTime,
    onChange: val => setActiveReadTime(val),
    options: READ_TIMES.map(r => ({
      value: r,
      label: r
    }))
  }];
  const handleReset = () => {
    setActiveCategory('All');
    setActiveReadTime('All');
    setSearchQuery('');
  };
  return <TroveLayout filterSections={filterSections} onResetFilters={handleReset} data-id="element-16482" data-mp="35g6d2ln952yh">
      {/* Header */}
      <div className="mb-8" data-id="element-16483" data-mp="30h3glplc8524">
        <div className="flex items-center gap-2 mb-2" data-id="element-16484" data-mp="14gbbezb0qlay">
          <BookOpenIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-16485" data-mp="14frrxe5pzout" />
          <Link to="/trove" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16486" data-mp="19e2ztjcgfpae">
            The Trove
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16487" data-mp="1e6cypovjrzot" />
          <Link to="/trove/mystery-thriller" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16488" data-mp="1j57d8emhf234">
            Mystery & Thriller
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-16489" data-mp="qg0g9k2oekjz" />
          <span className={`text-sm ${dk.textMuted}`} data-id="element-16490" data-mp="qicwnssscmux">Articles</span>
        </div>
        <div className="flex items-center gap-3 mb-3" data-id="element-16491" data-mp="vds9chf24cpr">
          <SwordIcon className="h-8 w-8 text-red-500" data-id="element-16492" data-mp="zhggiyzs16ah" />
          <h1 className={`text-3xl sm:text-4xl font-bold ${dk.textPrimary}`} data-id="element-16493" data-mp="zi9no12ndxt5">
            Mystery & Thriller Articles
          </h1>
        </div>
        <p className={`text-base max-w-2xl mb-6 ${dk.textMuted}`} data-id="element-16494" data-mp="2avkiarclvyq3">
          Uncover writing techniques, genre insights, and the art of suspense in
          mystery and thriller fiction.
        </p>
        <div className="max-w-md" data-id="element-16495" data-mp="3rc17euievj0l">
          <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Search mystery articles..." variant="pill" suggestions={[]} data-id="element-16496" data-mp="3rd2rfohenfug" />
        </div>
      </div>

      <div className="space-y-10" data-id="element-16497" data-mp="3cnesuv0lyvaf">
        {/* Featured Article */}
        {!searchQuery && activeCategory === 'All' && activeReadTime === 'All' && <section data-id="element-16498" data-mp="37qwqg1sr3919">
              <h2 className={`text-lg font-semibold mb-4 ${dk.textPrimary}`} data-id="element-16499" data-mp="33movvnzm2yv4">
                Featured
              </h2>
              <article className={`${dk.cardSubtle} rounded-xl shadow-sm border overflow-hidden`} data-id="element-16500" data-mp="2yoncf1y9wxj4">
                <div className="md:flex" data-id="element-16501" data-mp="11uu2ioh83h1p">
                  <div className="md:w-2/5" data-id="element-16502" data-mp="wws8j8i9dxbg">
                    <img src={featured.image} alt={featured.title} className="w-full h-52 md:h-full object-cover" data-id="element-16503" data-mp="wx1zozf1iaiq" />
                  </div>
                  <div className="md:w-3/5 p-6 flex flex-col justify-between" data-id="element-16504" data-mp="ascb7voez4q5">
                    <div data-id="element-16505" data-mp="3s67uaui4ngwl">
                      <div className="flex items-center gap-2 mb-3" data-id="element-16506" data-mp="3s4wqwvslgb60">
                        <span className={`px-2.5 py-0.5 text-white text-xs font-medium rounded-full ${rt.badgeBg}`} data-id="element-16507" data-mp="yvb69xoote1">
                          {featured.category}
                        </span>
                        <span className={`text-xs ${dk.textMuted}`} data-id="element-16508" data-mp="39853ca60r9w5">
                          {featured.date}
                        </span>
                      </div>
                      <h3 className={`text-xl font-bold mb-2 leading-snug ${dk.textPrimary}`} data-id="element-16509" data-mp="3e92jyibdrvvj">
                        {featured.title}
                      </h3>
                      <p className={`text-sm mb-4 line-clamp-3 ${dk.textSecondary}`} data-id="element-16510" data-mp="1cd8x398vmyrf">
                        {featured.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-16511" data-mp="36nyz01dhthf2">
                      <div className="text-xs" data-id="element-16512" data-mp="36pa4q6qi0z8t">
                        <p className={`font-semibold ${dk.textHeading}`} data-id="element-16513" data-mp="31vpb0qvxurff">
                          {featured.author}
                        </p>
                        <p className={`flex items-center gap-1 mt-0.5 ${dk.textMuted}`} data-id="element-16514" data-mp="3fpyid0bzcxl6">
                          <ClockIcon className="h-3 w-3" data-id="element-16515" data-mp="3pml19jkqxekk" />
                          {featured.readTime}
                        </p>
                      </div>
                      <button className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16516" data-mp="3kldunnucalfh">
                        Read full article →
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </section>}

        {/* Category quick-filters (inline chips) */}
        <div className="flex flex-wrap gap-2" data-id="element-16517" data-mp="30xloghayyrpt">
          {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${activeCategory === cat ? 'bg-red-100 text-red-700 ring-1 ring-red-200 dark:bg-red-900/40 dark:text-red-300 dark:ring-red-700' : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-16518" data-mp="2w0bxgx7xm7zv">
              {cat}
            </button>)}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between" data-id="element-16519" data-mp="bamlqt2iz2be">
          <p className={`text-sm ${dk.textMuted}`} data-id="element-16520" data-mp="6iusubvwcki3">
            {filtered.length} article{filtered.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
            {searchQuery ? ` matching "${searchQuery}"` : ''}
          </p>
          {(activeCategory !== 'All' || activeReadTime !== 'All' || searchQuery) && <button onClick={handleReset} className={`text-xs font-medium ${rt.accentLink}`} data-id="element-16521" data-mp="nu9u7eunn0mc">
              Clear filters
            </button>}
        </div>

        {/* Articles Grid */}
        {filtered.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-16522" data-mp="11qex8rnf3oni">
            {filtered.map(article => <ArticleCard key={article.id} article={article} data-id="element-16523" data-mp="16krhh3wpx40b" />)}
          </div> : <div className={`text-center py-16 rounded-xl border border-dashed ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`} data-id="element-16524" data-mp="1bjlyq1f1ekib">
            <BookOpenIcon className={`h-8 w-8 mx-auto mb-3 opacity-30 ${dk.textMuted}`} data-id="element-16525" data-mp="1lg78t15wylt0" />
            <p className={`text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-16526" data-mp="vpgw3ryf9dnt">
              No articles found
            </p>
            <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-16527" data-mp="15hcyvgbgdryd">
              Try adjusting your filters or search term
            </p>
            <button onClick={handleReset} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-16528" data-mp="10pv4jb3xqpmd">
              Clear all filters
            </button>
          </div>}
      </div>
    </TroveLayout>;
};
export default MysteryThrillerArticles;